import type { CategoryContent } from './row.js';

export const SPANISH_HISTORY: CategoryContent = {
  bscba: [
    [
      'Which Germanic people established a kingdom in Iberia after the Roman collapse?',
      [
        'The Visigoths',
        'The Ostrogoths',
        'The Franks',
        'The Vandals',
      ],
      0,
      'Visigoths ruled Iberia from the 5th to early 8th century, establishing a monarchy and legal codes. The Vandals briefly held southern regions before the Visigoths consolidated control.',
    ],
    [
      'In what year did Muslim armies first cross from North Africa into Iberia?',
      [
        '711 CE',
        '681 CE',
        '751 CE',
        '789 CE',
      ],
      0,
      'The Umayyad conquest began in 711 CE, traditionally led by Tariq ibn Ziyad, rapidly transforming the peninsula.',
    ],
    [
      'What was the name of the Christian Muslim-ruled territory in medieval Iberia?',
      [
        'Al-Andalus',
        'Granada',
        'Castilla',
        'Aragon',
      ],
      0,
      'Al-Andalus was the Islamic territory encompassing most of Iberia at its height (8th–15th centuries).',
    ],
    [
      'Which Christian kingdom initiated the Reconquista from its stronghold in the northern mountains?',
      [
        'Asturias',
        'Navarre',
        'Catalonia',
        'Galicia',
      ],
      0,
      'Asturias, in the northern mountains, is traditionally credited as the starting point of Christian reconquest, especially under Pelayo.',
    ],
    [
      'What was the title of the ruler of Al-Andalus, equivalent to a king?',
      [
        'Caliph',
        'Sultan',
        'Vizier',
        'Wazir',
      ],
      0,
      'Al-Andalus was ruled by caliphs (later emirs after the Umayyad collapse) representing Islamic authority.',
    ],
    [
      'Which pair of monarchs unified Spain through marriage and completed the Reconquista?',
      [
        'Ferdinand and Isabella',
        'Charles V and Isabella of Portugal',
        'Philip II and Mary of England',
        'James I and Margaret of France',
      ],
      0,
      'The Catholic Monarchs Ferdinand and Isabella married in 1469, uniting Aragon and Castile, and captured Granada in 1492.',
    ],
    [
      'What major ocean voyage did Spain sponsor in 1492?',
      [
        'Columbus\'s expedition to the Americas',
        'Vasco da Gama\'s voyage to India',
        'Magellan\'s circumnavigation of the globe',
        'John Cabot\'s exploration of North America',
      ],
      0,
      'Columbus\'s westward voyage, sponsored by the Catholic Monarchs, reached the Caribbean in 1492, beginning Spanish colonization of the Americas.',
    ],
    [
      'Which region of Spain was the last Islamic stronghold to fall during the Reconquista?',
      [
        'Granada',
        'Seville',
        'Cordoba',
        'Toledo',
      ],
      0,
      'Granada fell in 1492 to the Catholic Monarchs, ending nearly 800 years of Muslim rule in Iberia.',
    ],
    [
      'What was the Spanish Inquisition primarily focused on in the late 15th century?',
      [
        'Enforcing Catholic orthodoxy',
        'Suppressing Islamic rebellions',
        'Preventing Jewish immigration',
        'Controlling the nobility',
      ],
      0,
      'Established in 1478 by Ferdinand and Isabella, the Spanish Inquisition investigated heresy and enforced Catholic doctrine, including expulsion of unconverted Jews and Muslims.',
    ],
    [
      'Which Habsburg monarch oversaw Spain\'s greatest territorial extent in the 16th century?',
      [
        'Charles V',
        'Philip II',
        'Ferdinand I',
        'Maximilian II',
      ],
      0,
      'Charles V (r. 1516–1556) inherited Spain, the Netherlands, Austria, and parts of Italy, ruling the largest European empire of his era.',
    ],
    [
      'What was the primary goal of Spain\'s colonial administration in the Americas?',
      [
        'Extracting precious metals for the Crown',
        'Converting natives peacefully without military force',
        'Creating independent trading republics',
        'Establishing agricultural communes',
      ],
      0,
      'Spanish colonialism focused on exploiting resources (especially silver from Potosí) and establishing hierarchical settlements under direct Crown governance.',
    ],
    [
      'Which fleet was infamously defeated by the English in 1588?',
      [
        'The Spanish Armada',
        'The Invincible Fleet',
        'The Atlantic Squadron',
        'The Mediterranean Navy',
      ],
      0,
      'Philip II\'s Armada sailed to invade England in 1588 but was dispersed by storms and English forces, marking Spain\'s first major naval defeat.',
    ],
    [
      'Which conflict determined Spain\'s succession after Charles II\'s death in 1700?',
      [
        'The Spanish Succession War',
        'The Thirty Years\' War',
        'The War of Jenkins\' Ear',
        'The Peninsular War',
      ],
      0,
      'The War of Spanish Succession (1701–1714) pitted European powers over the Spanish throne, resulting in Bourbon rule replacing the Habsburgs.',
    ],
    [
      'Who was the Spanish general that led the uprising against Napoleon\'s occupation?',
      [
        'Francisco Goya',
        'King Ferdinand VII',
        'Juan Carlos I',
        'Miguel de Cervantes',
      ],
      0,
      'The Peninsular War (1808–1814) saw Spanish guerrillas and armies resist French occupation; Ferdinand VII became the rallying symbol though he was imprisoned in France.',
    ],
    [
      'What was the outcome of the Spanish-American War in 1898?',
      [
        'Spain lost Cuba, Puerto Rico, and the Philippines',
        'Spain retained all its colonies but lost naval dominance',
        'Spain gained new territories in Africa',
        'Spain and the US divided the Caribbean equally',
      ],
      0,
      'The 1898 war ended Spanish colonialism in the Americas and the Pacific, marking the definitive collapse of the Spanish Empire.',
    ],
  ],
  msc: [
    [
      'What was the primary administrative structure of Visigothic Spain that later influenced medieval kingdoms?',
      [
        'The liber iudiciorum law code',
        'A purely feudal system inherited from Rome',
        'Independent city-states without central authority',
        'Democratic assemblies modeled on Greek city-states',
      ],
      0,
      'The Visigothic liber iudiciorum created a written legal framework for royal governance that influenced Christian Spanish kingdoms.',
    ],
    [
      'Which Islamic dynasty ruled Al-Andalus during its greatest cultural and economic flourishing?',
      [
        'The Umayyad Caliphate',
        'The Abbasid Caliphate',
        'The Fatimid Caliphate',
        'The Ottoman Empire',
      ],
      0,
      'The Umayyad period, especially the 10th-century Caliphate of Córdoba, saw Al-Andalus become Europe\'s richest and most scientifically advanced region.',
    ],
    [
      'What triggered the fragmentation of Al-Andalus into taifas (petty kingdoms) in the 11th century?',
      [
        'The Umayyad Caliphate\'s collapse',
        'A Christian military invasion from the north',
        'Internal religious schisms between Sunni and Shia Muslims',
        'A massive plague that killed the ruling elite',
      ],
      0,
      'The Umayyad system collapsed c. 1031, leaving dozens of independent taifas vulnerable to Christian reconquest and Berber invasions.',
    ],
    [
      'Which Castilian ruler established a unified legal code applicable across his realms during the 13th century?',
      [
        'Alfonso X the Wise',
        'Ferdinand III, who conquered Seville',
        'James I of Aragon',
        'Sancho III of Navarre',
      ],
      0,
      'Alfonso X the Wise (r. 1252–1284) created the Siete Partidas, a comprehensive legal code centralizing royal authority and reducing noble feudal privileges.',
    ],
    [
      'What economic role did Al-Andalus play in Mediterranean and European trade networks?',
      [
        'It was a key trade intermediary with Christian Europe',
        'It was economically isolated and produced only agricultural goods',
        'It imported most manufactured goods from Italy',
        'It controlled all Mediterranean piracy and naval trade',
      ],
      0,
      'Al-Andalus functioned as a commercial hub linking the Islamic world to Christian Europe, exporting silk, agriculture, and metalwork.',
    ],
    [
      'Which military innovation did the Spanish employ extensively during the Reconquista?',
      [
        'Fortified frontier towns (presidios)',
        'Naval fleets to bypass enemy strongholds',
        'Early gunpowder cannons deployed systematically',
        'Mounted knights exclusively without infantry support',
      ],
      0,
      'Spanish kingdoms developed a frontier fortification system with military settlements defending newly conquered territories from reconquest.',
    ],
    [
      'What was the basis of the encomienda system in Spanish American colonies?',
      [
        'Granting colonists control of native labor',
        'Wages paid directly to indigenous workers by the Crown',
        'Slavery purchased from African slave traders',
        'Feudal contracts protecting indigenous rights equally with Spanish settlers',
      ],
      0,
      'The encomienda granted colonists tribute and labor rights over native peoples, justified as conversion and \'civilizing\' obligations but functioning as exploitation.',
    ],
    [
      'Which region became the center of Habsburg power in Spain and the seat of royal administration?',
      [
        'Castile, as the largest and strongest realm',
        'Aragon, due to its Mediterranean trade networks',
        'Granada, the most commercially developed',
        'Andalusia, the wealthiest agricultural region',
      ],
      0,
      'Castile\'s size and resources made it the dominant partner in the union; the Crown of Aragon became secondary despite initial equality.',
    ],
    [
      'What was the primary religious outcome of the Reconquista for newly conquered populations?',
      [
        'Forced conversion or expulsion of Muslims and Jews',
        'Religious pluralism guaranteed by Christian rulers',
        'Voluntary gradual conversion over centuries',
        'Integration of Islamic practices into Catholic worship',
      ],
      0,
      'Christians enforced conversion or expulsion: the 1492 Edict expelled unconverted Jews; Muslims faced increasing pressure culminating in 17th-century expulsions.',
    ],
    [
      'Which American silver deposits became the economic engine of Spain\'s 16th-century wealth?',
      [
        'Potosí in Bolivia',
        'Mexican gold mines',
        'Peruvian emerald fields',
        'Caribbean salt deposits',
      ],
      0,
      'Potosí\'s enormous silver production (accounting for perhaps 50% of global silver) flowed to Spain, funding Habsburg wars but also causing inflation.',
    ],
    [
      'What political conflict characterized the relationship between the Spanish Crown and the nobility during the 16th century?',
      [
        'The Crown subordinated aristocratic fueros',
        'Nobles successfully maintained feudal independence from royal authority',
        'The Crown offered nobility governance of American colonies without constraint',
        'Aristocrats and Crown shared power equally through a written constitution',
      ],
      0,
      'Habsburg monarchs, especially Charles V and Philip II, systematically reduced noble privileges and centralized authority, though they preserved noble estates.',
    ],
    [
      'How did Philip II\'s religious policies differ from those of his predecessors?',
      [
        'He intensified Counter-Reformation orthodoxy',
        'He relaxed Inquisition enforcement and permitted religious tolerance',
        'He allied with Protestant powers against Rome',
        'He established Spain as a secular state separating Church and Crown',
      ],
      0,
      'Philip II made religious uniformity a cornerstone of state policy, strengthening the Inquisition and aggressively rooting out Protestantism and crypto-Judaism.',
    ],
    [
      'What was the Treaty of Utrecht\'s primary impact on Spanish power in 1713?',
      [
        'Spain kept the Americas but lost European possessions',
        'Spain regained all its European territories',
        'Spain lost control of its American colonies to Britain',
        'Spain\'s monarchy was abolished in favor of a republic',
      ],
      0,
      'Utrecht ended Spanish domination in Europe: Spain kept the Americas and homeland but surrendered the Netherlands, Italy, and Gibraltar to rivals.',
    ],
    [
      'Which Enlightenment-era Spanish ruler attempted major administrative and economic reforms?',
      [
        'Charles III, the Bourbon reformer',
        'Charles II, the last Habsburg',
        'Ferdinand VI, who focused only on military expansion',
        'Philip V, who refused all reforms',
      ],
      0,
      'Charles III (r. 1759–1788) implemented Bourbon reforms: bureaucratic centralization, reduced clerical power, and Bourbon-style state administration.',
    ],
    [
      'What was the Peninsular War\'s geopolitical significance beyond its military outcome?',
      [
        'It showed popular nationalism could resist a continental power',
        'It consolidated French hegemony permanently over Spain',
        'It resulted in a negotiated independence for Spanish America',
        'It established Spain as a purely agrarian economy unable to industrialize',
      ],
      0,
      'The Spanish guerrilla resistance (1808–1814) inspired nationalist movements across Europe and the Americas, challenging Napoleonic supremacy.',
    ],
  ],
  phd: [
    [
      'What historiographical debate surrounds the origins and extent of Visigothic "decline" preceding the Islamic conquest?',
      [
        'Recent archaeology suggests institutional continuity, not collapse',
        'All scholars agree the Visigothic state was uniformly weak and internally divided',
        'Medieval sources accurately document internal civil wars without exaggeration',
        'The Visigoths had already abandoned Christianity by the 8th century',
      ],
      0,
      'Modern scholarship contests the traditional "decline and fall" narrative; some evidence suggests institutional stability, though civil succession disputes exploited by invaders.',
    ],
    [
      'How did the tax structure of Al-Andalus under the Umayyad Caliphate function across different religious communities?',
      [
        'The jizya on non-Muslims funded administration alongside Muslim zakat',
        'All residents paid identical taxes regardless of religion or status',
        'Non-Muslims paid no taxes but were excluded from commerce',
        'Religious communities collected taxes independently without central oversight',
      ],
      0,
      'The jizya and zakat created distinct fiscal statuses: non-Muslims\' tax revenue supported administration; Muslim zakat funded welfare, producing complex cross-subsidies.',
    ],
    [
      'What role did the Almohad and Almoravid invasions play in reshaping Al-Andalus\' political economy?',
      [
        'They centralized authority but disrupted taifa trade networks',
        'They had minimal impact; taifas remained independent and powerful',
        'They completely eliminated Islamic culture from Iberia',
        'They brought permanent peace and unified Al-Andalus permanently',
      ],
      0,
      'Almoravids (11th c.) and Almohads (12th c.) reasserted Islamic orthodoxy and centralized control, disrupting the sophisticated but fragmented taifa economy.',
    ],
    [
      'How did the economic organization of frontier presidios differ from interior Castilian settlements?',
      [
        'Frontier towns fell under martial law; interior towns kept fuero autonomy',
        'Frontier and interior towns had identical governance structures',
        'Interior towns were purely pastoral; frontier towns were purely military',
        'Frontier towns were autonomous; the interior was directly Crown-controlled',
      ],
      0,
      'Frontier presidios operated under stricter Crown military jurisdiction to secure newly conquered territories; interior towns preserved local fueros and traditional privileges.',
    ],
    [
      'What mechanisms allowed the Reconquista to progress unevenly across the peninsula (e.g., Castile faster than Aragon)?',
      [
        'Castile\'s larger population and centralized resources enabled faster conquest',
        'Aragon intentionally avoided reconquest for commercial reasons',
        'All regions conquered at identical rates',
        'Christian kingdoms never coordinated, making conquest random',
      ],
      0,
      'Castile\'s greater demographic resources and contiguous Islamic frontiers enabled faster reconquest; Aragon\'s Mediterranean orientation delayed Iberian expansion.',
    ],
    [
      'How did the Crown of Aragon\'s Mediterranean commercial networks influence its approach to colonial administration?',
      [
        'Aragon\'s merchants favored trading posts over conquest',
        'Aragon controlled all Spanish colonies exclusively',
        'Aragon rejected commerce and avoided the Americas entirely',
        'Both crowns pursued identical colonial strategies immediately',
      ],
      0,
      'Aragon\'s merchant class and Mediterranean focus initially pushed trading-post colonialism; Castile\'s dominance imposed territorial conquest as the colonial model.',
    ],
    [
      'What structural relationship existed between the Spanish Inquisition and royal fiscal policy?',
      [
        'Inquisition confiscations provided Crown revenue',
        'The Inquisition operated independently without Crown financial involvement',
        'Inquisition revenues went exclusively to the Church with zero Crown benefit',
        'The Inquisition had no confiscation powers',
      ],
      0,
      'The Crown franchised Inquisition prosecution; confiscated properties flowed to royal treasuries, creating perverse financial incentives for persecution.',
    ],
    [
      'How did the Habsburg interpretation of sovereignty differ from earlier Reconquista-era concepts of royal authority?',
      [
        'Habsburgs claimed divine-right sovereignty; earlier kings negotiated via fueros',
        'Earlier kings were absolute; Habsburgs were weak and subordinate to nobles',
        'Both employed identical forms of royal authority',
        'Sovereignty concepts did not change between periods',
      ],
      0,
      'Habsburgs asserted centralized divine-right monarchy; Reconquista-era crowns remained bound by negotiated noble privileges (fueros), creating structural differences.',
    ],
    [
      'What economic consequences followed from the 1609 Morisco expulsion for Spain\'s labor supply and fiscal base?',
      [
        'It created labor shortages and reduced Crown tax revenue',
        'The expulsion had no economic impact whatsoever',
        'Spain\'s economy improved immediately after expulsion',
        'The expelled Moriscos returned voluntarily',
      ],
      0,
      'Morisco expulsion depleted agricultural labor and rural tax revenue, contributing to Spain\'s relative economic decline in the 17th century.',
    ],
    [
      'How did the American treasure influx affect Spanish monetary policy and European inflation rates?',
      [
        'Silver imports fueled inflation, eroding Spanish competitiveness',
        'American silver had no effect on European prices',
        'Silver caused deflation across Europe',
        'Spain was the only country affected by price changes',
      ],
      0,
      'The 16th-century price revolution resulted partly from New World silver flooding European markets; Spain suffered most as domestic prices surged fastest.',
    ],
    [
      'What role did the Treaty of Westphalia (1648) play in reconfiguring Spanish geopolitical status?',
      [
        'It confirmed Spanish decline, ending Spain\'s European hegemony',
        'Spain gained new territories through the treaty',
        'Westphalia had no geopolitical consequences',
        'Spain became Europe\'s dominant power after Westphalia',
      ],
      0,
      'Westphalia codified Spanish weakness: Dutch independence, French expansion, and Sweden\'s rise ended Spanish dominance that had lasted since Charles V.',
    ],
    [
      'How did fiscal pressure from maintaining a global empire affect Spain\'s ability to invest in domestic manufacturing?',
      [
        'Military spending starved Spanish industry of capital',
        'Spain invested equally in manufacturing and military spending',
        'Spain focused entirely on manufacturing and neglected military needs',
        'Spain had unlimited resources for both manufacturing and warfare',
      ],
      0,
      'Empire costs drained capital from productive investment; Spain imported manufactured goods while becoming economically dependent on colonial tribute.',
    ],
    [
      'What administrative reforms did the Bourbon Succession Wars\' outcome impose on Spanish governance structures?',
      [
        'The Decree of Nueva Planta abolished Aragon\'s fueros',
        'Regional privileges were strengthened after the war',
        'No administrative changes occurred',
        'Spain became more decentralized after the war',
      ],
      0,
      'Philip V\'s Bourbon reforms eliminated Aragon, Valencia, and Mallorca\'s fueros, imposing Castilian centralized administration and reducing regional autonomy.',
    ],
    [
      'How did the Peninsular War\'s guerrilla tactics challenge contemporary military theory and European strategic assumptions?',
      [
        'Guerrilla forces showed they could resist regular armies',
        'Guerrillas were militarily insignificant and Europe ignored them',
        'Spain used only conventional military tactics',
        'Guerrilla warfare had been standard since ancient times with no new implications',
      ],
      0,
      'Spanish guerrillas (1808–1814) showed irregular forces could exhaust invaders through attrition and logistical disruption, revolutionizing military thinking.',
    ],
    [
      'What role did the Cortes of Cádiz (1812) play in establishing constitutional frameworks that influenced subsequent Spanish liberalism?',
      [
        'The Cortes created Spain\'s first constitutional document',
        'The Cortes had no constitutional significance',
        'Spain never developed constitutional traditions before 1975',
        'The Cortes abolished all monarchy and established a republic',
      ],
      0,
      'The Cádiz Constitution (1812) introduced parliamentary representation and limited monarchy, becoming a liberal reference point throughout 19th-century Spanish politics.',
    ],
  ],
  professor: [
    [
      'How do recent genetic and archaeological analyses challenge earlier assumptions about the genetic and cultural discontinuity between Visigothic and Islamic populations?',
      [
        'DNA evidence shows Berber and Arab admixture, not replacement',
        'Genetics conclusively prove complete population replacement with no mixing',
        'Archaeological evidence is irrelevant to understanding this period',
        'Recent science has not examined Visigothic-Islamic transitions',
      ],
      0,
      'Recent archaeogenetic work indicates admixture rather than replacement; settlement archaeology reveals continuity in domestic practices and local organization despite administrative change.',
    ],
    [
      'What historiographical significance does the Muwalladun concept hold in revising assumptions about Al-Andalus\' religious and ethnic categorization?',
      [
        'Muwalladun reveal fluid, not binary, religious boundaries',
        'Muwalladun were purely mythological with no historical basis',
        'Religious boundaries were absolute and hermetically sealed',
        'All populations converted instantly with no intermediate categories',
      ],
      0,
      'Muwalladun—Arabized Christians who maintained faith or converted strategically—reveal blurred religious identities and that sectarian categories obscure lived complexity.',
    ],
    [
      'How do recent analyses of the Umayyad administrative apparatus challenge assumptions about institutional coherence versus tribalism?',
      [
        'Records show sophisticated bureaucracy inherited from Byzantine models',
        'Umayyad administration was purely tribal with no formal structures',
        'Byzantines had no administrative influence on Umayyads',
        'Al-Andalus never developed any administrative systems',
      ],
      0,
      'Umayyad administrative records reveal continuity from Byzantine diwans; sophistication challenges earlier scholarship portraying Islamic governance as chaotic or purely personal.',
    ],
    [
      'What role did the repoblación concept play in organizing Castilian territorial expansion, and how did it reshape labor and property relations?',
      [
        'Repoblación shifted labor from feudal to wage-labor models',
        'Repoblación never occurred and populations remained sedentary',
        'Repoblación instantly created identical feudal structures everywhere',
        'Labor relationships never changed throughout the Reconquista',
      ],
      0,
      'Repoblación combined population incentives (fuero charters offering privileges) with property redistribution; it gradually weakened feudal labor bonds and created wage-dependent populations.',
    ],
    [
      'How do recent studies of taifa economies challenge assumptions about Al-Andalus\' decline as purely a military or political phenomenon?',
      [
        'Fragmentation destabilized trade networks and fiscal collection',
        'Economic decline was immediate and universal across all taifas',
        'Taifas had no economic impact on Mediterranean commerce',
        'Economies were identical before and after fragmentation',
      ],
      0,
      'Taifa fragmentation disrupted long-distance trade networks and tax collection efficiency rather than causing total collapse; local markets persisted but macroeconomic complexity declined.',
    ],
    [
      'What historical debate surrounds the extent of forced versus voluntary conversion among Islamic populations during the Reconquista?',
      [
        'Many conversions were voluntary, driven by social mobility',
        'All conversions were entirely voluntary with no coercion',
        'All conversions were entirely forced with zero voluntary participation',
        'No conversions occurred during the Reconquista',
      ],
      0,
      'Recent scholarship identifies mixed mechanisms: legal disability incentivized conversion; jizya tax burden created economic pressure; yet some converted for social advancement.',
    ],
    [
      'How do analyses of the encomienda\'s labor surplus mechanisms clarify its distinction from slavery in Spanish legal theory?',
      [
        'Encomienda granted usufruct of labor, not ownership',
        'Encomienda was identical to slavery in every respect',
        'Encomienda provided complete legal slavery without ambiguity',
        'Labor extraction mechanisms never developed in colonies',
      ],
      0,
      'Encomienda preserved subjects as Crown vassals (not property) while encomenderos controlled their labor, creating legal fiction obscuring de facto slavery.',
    ],
    [
      'What historiographical revisions have recent studies produced regarding the extent of Converso networks\' economic influence in Habsburg Spain?',
      [
        'Converso merchant and intellectual networks remained extensive',
        'Conversos had zero economic or intellectual influence',
        'Inquisition persecution had no real effects on Converso activity',
        'All Spanish merchants and intellectuals were Old Christians exclusively',
      ],
      0,
      'Recent scholarship documents deep Converso penetration of commerce, medicine, and law despite persecution; they remained economically crucial while under constant suspicion.',
    ],
    [
      'How did the rhetorical construct of limpieza de sangre (blood purity) function as an economic exclusion mechanism distinct from religious orthodoxy?',
      [
        'It excluded those with Jewish or Muslim ancestry from guilds and offices',
        'Limpieza had no economic consequences and was purely religious',
        'Limpieza benefited all social classes equally',
        'Blood purity laws never existed in Spanish law',
      ],
      0,
      'Limpieza statutes functioned as professional gatekeeping: excluding Conversos from guilds, universities, and offices preserved Old Christian monopolies on wealth and status.',
    ],
    [
      'What mechanisms of fiscal centralization did Charles V employ to subordinate regional crowns\' financial autonomy without formally abolishing fueros?',
      [
        'The Crown controlled strategic monopolies and fixed servicios',
        'Regional crowns retained complete financial independence',
        'Charles V immediately abolished all regional financial authority',
        'Fiscal mechanisms never changed during Charles V\'s reign',
      ],
      0,
      'Charles V monopolized strategic commodities and imposed fixed \'service\' payments; regions taxed locally but these operated within constraints, achieving dominance without formal abolition.',
    ],
    [
      'How do recent analyses of American treasure flows\' velocity and distribution explain Spain\'s paradoxical poverty despite silver abundance?',
      [
        'Rapid treasure circulation fueled inflation, not investment',
        'Spanish abundance of treasure made Spain uniformly wealthy',
        'Treasure had no monetary effects whatsoever',
        'Foreign merchants never participated in Spanish commerce',
      ],
      0,
      'Spanish inflation outpaced European averages as silver flooded; high domestic prices reduced Spanish manufactured exports\' competitiveness, enriching foreign traders importing goods.',
    ],
    [
      'What historiographical debate centers on whether Philip II\'s military expenditures represented rational state strategy or ideological excess?',
      [
        'Ideology and dynastic honor distorted rational cost-benefit analysis',
        'Military spending was purely rational with no ideological component',
        'Philip II had unlimited resources for any military adventure',
        'Military expenditures never affected Spanish finances',
      ],
      0,
      'Scholars debate whether Philip II\'s wars against Protestantism and the Ottomans reflected genuine strategic interests or ideological imperatives exceeding rational ROI.',
    ],
    [
      'How did the Decree of Nueva Planta\'s abolition of crown-of-Aragon fueros reshape the relationship between local institutions and centralized bureaucracy?',
      [
        'It subordinated regional councils to royal corregidores',
        'Aragon retained complete institutional independence after the decree',
        'The decree immediately created a unified Spanish government',
        'No administrative reorganization occurred after the War of Succession',
      ],
      0,
      'Nueva Planta dissolved crown-of-Aragon institutions\' authority, introducing Castilian corregidores and centralizing tax collection while maintaining titular regional crowns.',
    ],
    [
      'What role did the Napoleonic occupation\'s disruption of American trade monopolies play in Spain\'s postwar economic decline?',
      [
        'French blockade broke Spain\'s control of American commerce',
        'American trade continued unchanged throughout and after the Peninsular War',
        'Spain\'s American monopoly was irrelevant to overall prosperity',
        'The Peninsular War had no economic consequences',
      ],
      0,
      'The Napoleular Wars shattered Spain\'s American monopoly system; post-1814, Latin American independence permanently eliminated monopoly rents that had subsidized Spanish decline.',
    ],
    [
      'How do recent constitutional histories revise understanding of the Cádiz Cortes\' institutional innovations beyond their liberal ideology?',
      [
        'Cádiz pioneered property-blind electoral mechanisms and separation of powers',
        'Cádiz created nothing innovative institutionally',
        'Cádiz was purely ideological with no institutional effects',
        'Electoral mechanisms never developed before the 20th century',
      ],
      0,
      'The Cádiz Constitution established parliamentary committees, proportional-ish provincial representation, and judicial separation preceding later European models.',
    ],
  ],
};
